(ns common.ui.store
  (:require [common.spec :refer [eid?]]
            [datascript.core :as d]
            [re-frame.core :as rf]
            [re-posh.core :as rp]))

(rp/reg-event-ds :ui.store/set-value
  (fn [_ [_ e attr value]]
    [[:db/add e attr value]]))

(rp/reg-event-ds :ui.store/unset-value
  (fn [_ [_ e attr]]
    [[:db.fn/retractAttribute e attr]]))

(rp/reg-query-sub :ui.store/value
  '[:find ?v .
    :in $ ?e ?attr
    :where [?e ?attr ?v]])

(rp/reg-query-sub :ui.store/values-by-attr
  '[:find [?v ...]
    :in $ ?attr
    :where [_ ?attr ?v]])

(rp/reg-query-sub :ui.store/values
  '[:find [?v ...]
    :in $ ?e ?attr
    :where [?e ?attr ?v]])

(rp/reg-query-sub :ui.store/lookup
  '[:find [?e ...]
    :in $ ?attr ?v
    :where [?e ?attr ?v]])

(rp/reg-sub :ui.store/pull-entity
  (fn [_ [_ pattern id]]
    {:type :pull
     :pattern pattern
     :id id}))

(defprotocol DataDomainEntity
  "Allows access to -and mutation of a specific data entity by wrapping a
  reference/pointer/primary key to the piece of data. The data can be stored
  anywhere, it depends on the specific implementations of this protocol how the
  data is retrieved or updated."
  (identifier [this] "Return unique identifier of entity")
  (entity [this] [this selection] "Get the full data, optionally
  limited/extended by the selection. It depends on the implementation for the
  specific data source whether extension is allowed and the exact syntax of the
  selection argument")
  (set-value! [this k v] "Set or reset value at location k within the data
  entity with value v (similar to `assoc')")
  (unset-value! [this k] "Remove the location k and associated value (similar to
  `dissoc')")
  (get-value [this k] "Get value at location k (similar to
  `get' but without option to set fallback)"))

(defrecord DataScriptDBEntity [eid]
  DataDomainEntity
  (identifier [_] eid)
  (entity [this]
    (entity this '[*]))
  (entity [_ selection]
    ;; the selection should be a datomic pull specification, this even allows
    ;; extending the returned entity with nested data
    (rp/subscribe [:ui.store/pull-entity selection eid]))

  (set-value! [this k v]
    (if-let [setter (get-in this [:custom-setters k])]
      (cond (vector? setter) (rp/dispatch (conj setter eid v))
            (ifn? setter) (setter eid v))
      (if (some? v)
        (rp/dispatch [:ui.store/set-value eid k v])
        ;; datascript does not allow nil values, so if nil given, delete the datom
        (unset-value! this k))))

  (unset-value! [_ k]
    (rp/dispatch [:ui.store/unset-value eid k]))

  (get-value [this k]
    (if-let [getter (get-in this [:custom-getters k])]
      (cond (vector? getter) (rp/subscribe (conj getter eid))
            (ifn? getter) (getter eid))
      (rp/subscribe [:ui.store/value eid k]))))

(rf/reg-event-db :ui.store/update-in-app-db
  (fn [db [_ ks f & args]]
    (apply update-in db ks f args)))

(rf/reg-sub :ui.store/app-db-entity
  (fn [db [_ path selection]]
    (let [v (get-in db path)]
      (cond-> v
        selection (select-keys selection)))))

(defrecord AppDBEntity [path]
  DataDomainEntity
  (identifier [_] path)
  (entity [this]
    (entity this nil))

  (entity [_ selection]
    (rf/subscribe [:ui.store/app-db-entity path selection]))

  (set-value! [_ k v]
    (rf/dispatch [:ui.store/update-in-app-db path assoc k v]))

  (unset-value! [_ k]
    (rf/dispatch [:ui.store/update-in-app-db path dissoc k]))

  (get-value [_ k]
    (rf/subscribe [:ui.store/app-db-entity (conj path k) nil])))

(rp/reg-event-ds :ui.store/delete
  ;; delete one or more entities, possibly part of a sequence
  ;; Usage:
  ;; - pass single entity id
  ;; - pass list/vector/set of entity ids
  ;; - if the entity is part of a sequence, also
  ;;   pass sequence relation attribute and position
  ;;   attribute keywords
  (fn [db [_ ids seq-rel pos-attr]]
    (let [ids (cond-> ids (eid? ids) vector)]
      (apply concat
             (for [id ids]
               (cond-> (list [:db/retractEntity id])
                 (and seq-rel pos-attr)
                 (conj [:db.sequence/reset
                        (some :e (d/datoms db :avet seq-rel id))
                        seq-rel id pos-attr])))))))
